/* import __COLOCATED_TEMPLATE__ from './fin-readiness-bar.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { use } from 'ember-resources/util/function-resource';
import type IntlService from 'ember-intl/services/intl';
import {
  filters,
  and,
} from 'embercom/components/reporting/bespoke/overview/conversation-funnel/series-config';

interface Args {
  reportState: ReportState;
  totalVolume: string;
  shouldShowPersonalized: boolean;
  selectedIndustryText: string;
  selectedIndustry: string;
}

const QUERY_TYPE_STRINGS: Record<string, QueryTypeStrings> = {
  informational: {
    label:
      'automation.ai-insights.fin-readiness.automation-opportunity.automation-potential.subcategory-informational',
    bgColor: 'bg-vis-violet-50',
  },
  personalized: {
    label:
      'automation.ai-insights.fin-readiness.automation-opportunity.automation-potential.subcategory-personalized',
    bgColor: 'bg-vis-violet-60',
  },
  actions: {
    label:
      'automation.ai-insights.fin-readiness.automation-opportunity.automation-potential.subcategory-tasks',
    bgColor: 'bg-vis-violet-70',
  },
  complex: {
    label: '',
    bgColor: 'bg-vis-olive-60',
  },
};

interface QueryTypeStrings {
  label: string;
  bgColor: string;
}

interface IndustryPercentages {
  informational: string;
  personalized: string;
  actions: string;
  complex: string;
}

// hardcoded values for generic version of the page
// For more information, see this issue: https://github.com/intercom/intercom/issues/441095
const INDUSTRY_PERCENTAGES: Record<string, IndustryPercentages> = {
  other: {
    informational: '39',
    personalized: '30',
    actions: '20',
    complex: '11',
  },
  'software-technology': {
    informational: '47',
    personalized: '18',
    actions: '18',
    complex: '17',
  },
  'gaming-gambling': {
    informational: '27',
    personalized: '48',
    actions: '17',
    complex: '8',
  },
  ecommerce: {
    informational: '34',
    personalized: '28',
    actions: '32',
    complex: '6',
  },
  'financial-services': {
    informational: '39',
    personalized: '36',
    actions: '16',
    complex: '9',
  },
};

export default class FinReadinessBar extends Component<Args> {
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare intl: IntlService;

  private static readonly COMPLEX_KEY = 'complex';
  private static readonly HUMAN_TOOLTIP_KEY = 'human';

  @use conversationsWithQueryTypeResource = staticDataResource(this, {
    serializedChart: () => this.queryTypeBreakdownChartDefinition,
    reportState: () => this.args.reportState,
  });

  @use teammateHandledConversationsResource = staticDataResource(this, {
    serializedChart: () => this.teammateHandledConversationsChartDefinition,
    reportState: () => this.args.reportState,
  });

  @use totalConversationsResource = staticDataResource(this, {
    serializedChart: () => this.totalConversationsChartDefinition,
    reportState: () => this.args.reportState,
  });

  get queryTypeFilter() {
    return {
      type: 'exists',
      data: {
        property: 'conversation_custom_fields#ai_insights_query_type',
        attribute: 'conversation_custom_fields#ai_insights_query_type',
      },
    };
  }

  get teammateHandledConversationsFilter() {
    return and(filters.teammateWasEverInvolved, filters.finWasNeverInvolved);
  }

  get teammateHandledConversationsChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: {
            type: 'and',
            filters: [this.teammateHandledConversationsFilter],
          },
        },
      ],
    };
  }

  get totalConversationsChartDefinition() {
    return {
      visualization_type: 'counter',
      view_by: null,
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
        },
      ],
    };
  }

  get queryTypesData() {
    return Object.keys(QUERY_TYPE_STRINGS)
      .map((key) => this.buildQueryTypeData(key))
      .filter(Boolean); // Remove null entries
  }

  private buildQueryTypeData(key: string) {
    let breakdownValue = this.getBreakdownValueForKey(key);
    if (!breakdownValue) {
      return null;
    }

    let tooltipData = this.getTooltipTextForQueryType(
      this.getTooltipKey(key),
      (breakdownValue.raw as number) || 0,
    );

    return {
      label: this.getTranslatedLabel(key),
      bgColor: QUERY_TYPE_STRINGS[key].bgColor,
      percentage: breakdownValue.percentage.toFixed(0),
      percentageLabel: this.getPercentageLabel(key, breakdownValue),
      rawValue: breakdownValue.raw,
      tooltipTextTitle: tooltipData.tooltipTextTitle,
      tooltipTextDescription: tooltipData.tooltipTextDescription,
    };
  }

  private getBreakdownValueForKey(key: string) {
    return key === FinReadinessBar.COMPLEX_KEY
      ? this.breakdownValue[FinReadinessBar.COMPLEX_KEY]
      : this.breakdownValue[key];
  }

  private getTranslatedLabel(key: string): string {
    return QUERY_TYPE_STRINGS[key].label ? this.intl.t(QUERY_TYPE_STRINGS[key].label) : '';
  }

  private getPercentageLabel(key: string, breakdownValue: any): string {
    return key === FinReadinessBar.COMPLEX_KEY ? '' : `${breakdownValue.percentage.toFixed(0)}%`;
  }

  private getTooltipKey(key: string): string {
    return key === FinReadinessBar.COMPLEX_KEY ? FinReadinessBar.HUMAN_TOOLTIP_KEY : key;
  }

  getHardcodedPercentagesForIndustry(industry: string): IndustryPercentages {
    let percentage = INDUSTRY_PERCENTAGES[industry] || INDUSTRY_PERCENTAGES['other'];
    return percentage;
  }

  shouldShowLegend(queryType: any): boolean {
    return queryType.label !== '';
  }

  get genericQueryTypesData() {
    let hardcodedPercentages = this.getHardcodedPercentagesForIndustry(this.args.selectedIndustry);

    return Object.keys(QUERY_TYPE_STRINGS)
      .map((key) => {
        let percentage = hardcodedPercentages[key as keyof IndustryPercentages];
        let label = QUERY_TYPE_STRINGS[key].label ? this.intl.t(QUERY_TYPE_STRINGS[key].label) : '';

        return {
          key,
          bgColor: QUERY_TYPE_STRINGS[key].bgColor,
          percentage,
          label,
          tooltipTitle: this.getGenericTooltipTitle(key, percentage),
          tooltipText: this.getGenericTooltipText(key),
        };
      })
      .filter(Boolean);
  }

  getGenericTooltipTitle(key: string, percentage: string) {
    let titleKey = `automation.ai-insights.fin-readiness.automation-opportunity.generic-automation-opportunity.${key}-title`;
    return this.intl.t(titleKey, { percentage });
  }

  getGenericTooltipText(key: string) {
    let tooltipKey = `automation.ai-insights.fin-readiness.automation-opportunity.generic-automation-opportunity.${key}-tooltip`;
    return this.intl.t(tooltipKey, { htmlSafe: true });
  }

  get humanTooltipData() {
    return {
      title: this.intl.t(
        'automation.ai-insights.fin-readiness.automation-opportunity.generic-automation-opportunity.complex-title',
        { percentage: this.automationPotentialValues.remainingHumanPercentage.toString() },
      ),
      text: this.intl.t(
        'automation.ai-insights.fin-readiness.automation-opportunity.generic-automation-opportunity.complex-tooltip',
        { htmlSafe: true },
      ),
    };
  }

  get genericAutomationTitle() {
    let isGeneralIndustry = this.args.selectedIndustry === 'unknown-industry';
    let translationKey = isGeneralIndustry
      ? 'automation.ai-insights.fin-readiness.automation-opportunity.generic-automation-opportunity.general-title'
      : 'automation.ai-insights.fin-readiness.automation-opportunity.generic-automation-opportunity.title';

    return this.intl.t(translationKey, {
      count: this.automationPotentialValues.finPercentage,
      selectedIndustry: this.args.selectedIndustryText,
      htmlSafe: true,
    });
  }

  getTooltipTextForQueryType(key: string, rawValue: number) {
    let percentage = this.intl.formatNumber(rawValue / this.totalQueryTypeConversations, {
      style: 'percent',
    });

    let titleKey = `automation.ai-insights.fin-readiness.automation-opportunity.automation-potential.tooltip-${key}-title`;
    let descriptionKey = `automation.ai-insights.fin-readiness.automation-opportunity.automation-potential.tooltip-${key}-description`;

    return {
      tooltipTextTitle: this.intl.t(titleKey, { percentage, htmlSafe: true }),
      tooltipTextDescription: this.intl.t(descriptionKey),
    };
  }

  get humanTooltipText() {
    let totalConversations = this.totalQueryTypeConversations;
    let automatableConversations = this.queryTypesData
      .filter(
        (queryType): queryType is NonNullable<typeof queryType> =>
          queryType !== null && queryType.label !== '',
      )
      .reduce((sum, queryType) => sum + ((queryType.rawValue as number) || 0), 0);

    let percentageHumanConversations =
      (totalConversations - automatableConversations) / totalConversations;

    let percentage = this.intl.formatNumber(percentageHumanConversations, { style: 'percent' });

    return {
      tooltipTextTitle: this.intl.t(
        'automation.ai-insights.fin-readiness.automation-opportunity.automation-potential.tooltip-human-title',
        { percentage, htmlSafe: true },
      ),
      tooltipTextDescription: this.intl.t(
        'automation.ai-insights.fin-readiness.automation-opportunity.automation-potential.tooltip-human-description',
      ),
    };
  }

  get totalConversations() {
    if (!this.totalConversationsResource.flatData || !this.totalConversationsResource.flatData[0]) {
      return 0;
    }
    return this.totalConversationsResource.kpiData[0] as number;
  }

  get queryTypeBreakdownChartDefinition() {
    return {
      visualization_type: 'horizontal_bar',
      stacked: true,
      view_by: 'conversation_custom_fields#ai_insights_query_type',
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          filters: {
            type: 'and',
            filters: [and(this.queryTypeFilter, filters.finWasNeverInvolved)],
          },
        },
      ],
    };
  }

  get breakdownValue() {
    if (
      !this.conversationsWithQueryTypeResource.flatData ||
      !this.conversationsWithQueryTypeResource.flatData[0]
    ) {
      // Fallback data for development/testing
      return {
        informational: { raw: 600, percentage: 60 },
        personalized: { raw: 200, percentage: 20 },
        actions: { raw: 100, percentage: 10 },
        complex: { raw: 100, percentage: 10 },
      };
    }

    return Object.fromEntries(
      this.conversationsWithQueryTypeResource.flatData[0].map(([key, value]) => [
        key,
        {
          raw: value,
          percentage:
            this.totalQueryTypeConversations > 0
              ? Math.round(((value as number) / this.totalQueryTypeConversations) * 100)
              : 0,
        },
      ]),
    );
  }

  get isLoading() {
    return (
      this.conversationsWithQueryTypeResource.isLoading ||
      this.teammateHandledConversationsResource.isLoading ||
      this.totalConversationsResource.isLoading
    );
  }

  get teammateHandledConversations() {
    if (
      !this.teammateHandledConversationsResource.kpiData ||
      !this.teammateHandledConversationsResource.kpiData[0]
    ) {
      return 0;
    }
    return this.teammateHandledConversationsResource.kpiData[0] as number;
  }

  get totalQueryTypeConversations() {
    if (
      !this.conversationsWithQueryTypeResource.kpiData ||
      !this.conversationsWithQueryTypeResource.kpiData[0]
    ) {
      return 0;
    }
    return this.conversationsWithQueryTypeResource.flatData[0].reduce(
      (acc, [, value]) => acc + (value as number),
      0,
    );
  }

  get automatedQueryTypeConversations() {
    if (
      !this.conversationsWithQueryTypeResource.kpiData ||
      !this.conversationsWithQueryTypeResource.kpiData[0]
    ) {
      return 0;
    }
    return this.conversationsWithQueryTypeResource.flatData[0]
      .filter(([key]) => key !== 'complex')
      .reduce((acc, [, value]) => acc + (value as number), 0);
  }

  get complexQueryTypeConversations() {
    if (
      !this.conversationsWithQueryTypeResource.kpiData ||
      !this.conversationsWithQueryTypeResource.kpiData[0]
    ) {
      return 0;
    }
    return this.conversationsWithQueryTypeResource.flatData[0]
      .filter(([key]) => key === FinReadinessBar.COMPLEX_KEY)
      .reduce((acc, [, value]) => acc + (value as number), 0);
  }

  get currentStateValues() {
    let totalConversations = this.totalConversations;
    let humanConversations = this.teammateHandledConversations;
    let queryTypeConversations = this.totalQueryTypeConversations;
    let automatableQueryTypeConversations = this.automatedQueryTypeConversations;

    return {
      humanPercentage:
        totalConversations > 0 ? Math.round((humanConversations / totalConversations) * 100) : 0,
      automatedPercentage:
        totalConversations > 0
          ? Math.round((automatableQueryTypeConversations / queryTypeConversations) * 100)
          : 0,
      snapshotCount: queryTypeConversations,
    };
  }

  get automationPotentialValues() {
    if (!this.args.shouldShowPersonalized) {
      let hardcodedPercentages = this.getHardcodedPercentagesForIndustry(
        this.args.selectedIndustry,
      );
      let finPercentage =
        parseInt(hardcodedPercentages.informational, 10) +
        parseInt(hardcodedPercentages.personalized, 10) +
        parseInt(hardcodedPercentages.actions, 10);
      let remainingHumanPercentage = 100 - finPercentage;

      return {
        finPercentage,
        remainingHumanPercentage,
        finCount: finPercentage,
        totalCount: 100,
      };
    }

    let queryTypeConversations = this.totalQueryTypeConversations;
    let automatableQueryTypeConversations = this.automatedQueryTypeConversations;
    let complexQueryTypeConversations = this.complexQueryTypeConversations;

    return {
      finPercentage:
        queryTypeConversations > 0
          ? Math.round((automatableQueryTypeConversations / queryTypeConversations) * 100)
          : 0,
      remainingHumanPercentage:
        queryTypeConversations > 0
          ? Math.round((complexQueryTypeConversations / queryTypeConversations) * 100)
          : 0,
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::FinReadiness::FinReadinessBar': typeof FinReadinessBar;
  }
}
